/**
 * PaginationBar — componente di paginazione condiviso per tutti gli
 * elenchi del gestionale (20/02/2026).
 *
 * Mostra:
 *   - Range "X – Y di Z voci" sulla sinistra.
 *   - Selettore "Righe per pagina" (10 / 30 / 50 / 100).
 *   - Controlli pagina « ‹ 1 2 … N › » sulla destra.
 *
 * Design:
 *   - "Compact pagination": fino a 7 pagine totali mostriamo tutti
 *     i numeri; oltre, una finestra di 3 numeri attorno alla
 *     corrente + ellipsis + prima/ultima pagina.
 *   - Quando totalPages = 1, i numeri di pagina vengono nascosti
 *     ma il selettore "Righe per pagina" resta visibile, così
 *     l'operatore può comunque scegliere un page-size per liste
 *     che potrebbero crescere.
 *   - Quando `autoHide` è true E total ≤ minimo dei pageSizes,
 *     l'intera barra viene nascosta (per liste sempre piccole).
 *   - `data-testid` su ogni controllo per regression test.
 *
 * Stile coerente col resto del gestionale: bordi slate-200, hover
 * blu, font-medium, padding compatto. Niente icone aggiuntive
 * importate — usa solo testo + chevron unicode.
 */
import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const buildPageWindow = (page, totalPages) => {
  // Restituisce un array di numeri o "…" da renderizzare.
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const window = new Set([1, totalPages, page, page - 1, page + 1]);
  // Mostra anche 2 e totalPages-1 quando si è vicini ai bordi
  if (page <= 3) window.add(2).add(3);
  if (page >= totalPages - 2) window.add(totalPages - 1).add(totalPages - 2);
  const nums = Array.from(window)
    .filter((n) => n >= 1 && n <= totalPages)
    .sort((a, b) => a - b);
  // Inserisce "…" dove c'è gap.
  const out = [];
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push("…");
    out.push(n);
  });
  return out;
};

export function PaginationBar({
  page,
  pageSize,
  pageSizes,
  total,
  totalPages,
  rangeStart,
  rangeEnd,
  onPageChange,
  onPageSizeChange,
  itemLabelSingular = "voce",
  itemLabelPlural = "voci",
  autoHide = false,
}) {
  // Mini "Vai a pagina…" — visibile solo quando ci sono abbastanza
  // pagine da renderlo davvero utile (>5). Per liste corte il page
  // picker numerico è già completo. L'input riflette la pagina
  // corrente quando cambia da altri controlli (es. ›, »).
  // NB: hooks dichiarati PRIMA del return anticipato per rispettare
  // le rules-of-hooks di React.
  const [jumpValue, setJumpValue] = useState(String(page));
  useEffect(() => { setJumpValue(String(page)); }, [page]);

  if (autoHide || total === 0) {
    // Niente da paginare: barra nascosta.
    return null;
  }

  const window = buildPageWindow(page, totalPages);
  const goTo = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    onPageChange(p);
  };

  const submitJump = () => {
    const n = Number.parseInt(jumpValue, 10);
    if (!Number.isFinite(n)) { setJumpValue(String(page)); return; }
    const clamped = Math.min(totalPages, Math.max(1, n));
    if (clamped !== page) onPageChange(clamped);
    setJumpValue(String(clamped));
  };

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 py-2 px-1 text-sm border-t border-slate-100"
      data-testid="pagination-bar"
      role="navigation"
      aria-label="Paginazione"
    >
      <div className="text-slate-600">
        {total === 1 ? (
          <>1 {itemLabelSingular}</>
        ) : (
          <>
            <span className="font-medium text-slate-700">{rangeStart}–{rangeEnd}</span>
            <span className="text-slate-400"> di </span>
            <span className="font-medium text-slate-700">{total}</span>
            <span className="text-slate-400"> {itemLabelPlural}</span>
          </>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Righe per pagina</span>
          <Select value={String(pageSize)} onValueChange={onPageSizeChange}>
            <SelectTrigger
              className="h-8 w-[78px] text-sm"
              data-testid="pagination-page-size"
              aria-label="Righe per pagina"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizes.map((s) => (
                <SelectItem key={s} value={String(s)} data-testid={`pagination-page-size-${s}`}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-200 text-slate-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              onClick={() => goTo(1)}
              disabled={page === 1}
              aria-label="Prima pagina"
              data-testid="pagination-first"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-200 text-slate-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              onClick={() => goTo(page - 1)}
              disabled={page === 1}
              aria-label="Pagina precedente"
              data-testid="pagination-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {window.map((p, idx) =>
              p === "…" ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="h-8 w-8 inline-flex items-center justify-center text-slate-400 select-none"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={`h-8 min-w-[2rem] px-2 inline-flex items-center justify-center rounded border text-sm font-medium transition-colors ${
                    p === page
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "border-slate-200 text-slate-700 hover:bg-blue-50 hover:text-blue-700"
                  }`}
                  onClick={() => goTo(p)}
                  aria-current={p === page ? "page" : undefined}
                  data-testid={`pagination-page-${p}`}
                >
                  {p}
                </button>
              )
            )}

            <button
              type="button"
              className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-200 text-slate-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              onClick={() => goTo(page + 1)}
              disabled={page === totalPages}
              aria-label="Pagina successiva"
              data-testid="pagination-next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-200 text-slate-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              onClick={() => goTo(totalPages)}
              disabled={page === totalPages}
              aria-label="Ultima pagina"
              data-testid="pagination-last"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>

            {totalPages > 5 && (
              <div
                className="flex items-center gap-1 ml-1 pl-2 border-l border-slate-200"
                data-testid="pagination-jump"
              >
                <span className="text-xs text-slate-500">Vai a</span>
                <Input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={jumpValue}
                  onChange={(e) => setJumpValue(e.target.value)}
                  onBlur={submitJump}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); submitJump(); }
                  }}
                  className="h-8 w-14 px-1.5 text-sm text-center"
                  aria-label={`Vai a pagina (1–${totalPages})`}
                  data-testid="pagination-jump-input"
                />
                <span className="text-xs text-slate-400">/ {totalPages}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
